import { useState, useEffect } from 'react'
import ReactNativeHapticFeedback from 'react-native-haptic-feedback'
import { Hints } from './components/hints/hints'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { SettingsModal } from './components/modals/SettingsModal'
import {
  WIN_MESSAGES,
  GAME_COPIED_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  CORRECT_WORD_MESSAGE,
} from './constants/strings'
import {
  MAX_WORD_LENGTH,
  MAX_CHALLENGES,
  REVEAL_TIME_MS,
  GAME_LOST_INFO_DELAY,
  WELCOME_INFO_MODAL_MS,
} from './constants/settings'
import { isWinningWord, solution, unicodeLength } from './lib/words'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import {
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
  setStoredIsHighContrastMode,
  getStoredIsHighContrastMode,
} from './lib/localStorage'
import { default as GraphemeSplitter } from 'grapheme-splitter'

import './App.css'
import { AlertContainer } from './components/alerts/AlertContainer'
import { useAlert } from './context/AlertContext'
import { Navbar } from './components/navbar/Navbar'
import ReactGA from 'react-ga4'
const trackingId = process.env.REACT_APP_GOOGLE_MEASUREMENT_ID ?? 'none' // Replace with your Google Analytics tracking ID
//console.log(trackingId)
ReactGA.initialize([
  {
    trackingId: trackingId,
    // gaOptions: {...}, // optional
    // gtagOptions: {...}, // optional
  },
])
// ReactGA.set({
//   //username: localStorage.getItem('userName'),
//   // Other relevant user information
// })

function App() {
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches

  const { showError: showErrorAlert, showSuccess: showSuccessAlert } =
    useAlert()
  const [currentGuess, setCurrentGuess] = useState('')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [currentRowClass, setCurrentRowClass] = useState('')
  const [isGameLost, setIsGameLost] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme')
      ? localStorage.getItem('theme') === 'dark'
      : prefersDarkMode
      ? true
      : false
  )
  const [isHighContrastMode, setIsHighContrastMode] = useState(
    getStoredIsHighContrastMode()
  )
  const [isRevealing, setIsRevealing] = useState(false)
  const [guesses, setGuesses] = useState<string[]>(() => {
    const loaded = loadGameStateFromLocalStorage()
    if (loaded?.solution !== solution) {
      return []
    }
    const gameWasWon = loaded.guesses.includes(solution)
    if (gameWasWon) {
      setIsGameWon(true)
      ReactGA.event({
        category: 'reloaded',
        action: 'reloaded_won',
        label: 'won',
        nonInteraction: true,
      })
    }
    if (loaded.guesses.length === MAX_CHALLENGES && !gameWasWon) {
      ReactGA.event({
        category: 'reloaded',
        action: 'reloaded_lost',
        label: 'lost',
        nonInteraction: true,
      })
      setIsGameLost(true)
      ReactNativeHapticFeedback.trigger('notificationError', options)
      showErrorAlert(CORRECT_WORD_MESSAGE(solution), {
        persist: true,
      })
    }
    return loaded.guesses
  })

  const [stats, setStats] = useState(() => loadStats())

  const options = {
    enableVibrateFallback: true,
    ignoreAndroidSystemSettings: false,
  }

  const [isHardMode] = useState(
    localStorage.getItem('gameMode')
      ? localStorage.getItem('gameMode') === 'hard'
      : false
  )

  useEffect(() => {
    // if no game state on load,
    // show the user the how-to info modal
    if (!loadGameStateFromLocalStorage()) {
      setTimeout(() => {
        setIsInfoModalOpen(true)
      }, WELCOME_INFO_MODAL_MS)
    }
  }, [])

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    if (isHighContrastMode) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [isDarkMode, isHighContrastMode])

  const handleDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
  }

  const handleHardMode = (isHard: boolean) => {
    if (guesses.length === 0 || localStorage.getItem('gameMode') === 'hard') {
      //   setIsHardMode(isHard)
      localStorage.setItem('gameMode', isHard ? 'hard' : 'normal')
    } else {
      //   showErrorAlert(HARD_MODE_ALERT_MESSAGE)
    }
  }

  const handleHighContrastMode = (isHighContrast: boolean) => {
    setIsHighContrastMode(isHighContrast)
    setStoredIsHighContrastMode(isHighContrast)
  }

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  useEffect(() => {
    saveGameStateToLocalStorage({ guesses, solution })
  }, [guesses])

  useEffect(() => {
    if (isGameWon) {
      const winMessage =
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      const delayMs = REVEAL_TIME_MS * MAX_WORD_LENGTH

      showSuccessAlert(winMessage, {
        delayMs,
        onClose: () => setIsStatsModalOpen(true),
      })
    }

    if (isGameLost) {
      setTimeout(() => {
        setIsStatsModalOpen(true)
      }, GAME_LOST_INFO_DELAY)
    }
  }, [isGameWon, isGameLost, showSuccessAlert])

  const onChar = (value: string) => {
    if (
      unicodeLength(`${currentGuess}${value}`) <= MAX_WORD_LENGTH &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setCurrentGuess(`${currentGuess}${value}`)
    }
  }

  const onDelete = () => {
    setCurrentGuess(
      new GraphemeSplitter().splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = (skipped: boolean) => {
    if (isGameWon || isGameLost) {
      return
    }

    if (!skipped && !(unicodeLength(currentGuess) === MAX_WORD_LENGTH)) {
      setCurrentRowClass('jiggle')
      var element3 = document.getElementById('hints-box')
      if (element3 !== null) {
        element3.scrollIntoView(false)
        element3.scrollIntoView({ block: 'end' })
        element3.scrollTop = element3.scrollHeight
      }
      var element12 = document.getElementById('whole-div')
      if (element12 !== null) {
        // element1.scrollIntoView(false);
        // element1.scrollIntoView({block: "end"});
        element12.scrollTop = element12.scrollHeight
      }

      const scrollingElement = document.scrollingElement || document.body
      scrollingElement.scrollTop = scrollingElement.scrollHeight
      ReactNativeHapticFeedback.trigger('notificationWarning', options)
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        onClose: clearCurrentRowClass,
      })
    }

    // if (!isWordInWordList(currentGuess)) {
    //   setCurrentRowClass('jiggle')
    //   return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
    //     onClose: clearCurrentRowClass,
    //   })
    // }

    // enforce hard mode - all guesses must contain all previously revealed letters
    if (isHardMode) {
      // const firstMissingReveal = findFirstUnusedReveal(currentGuess, guesses)
      // if (firstMissingReveal) {
      //   setCurrentRowClass('jiggle')
      //   return showErrorAlert(firstMissingReveal, {
      //     onClose: clearCurrentRowClass,
      //   })
      // }
    }

    setIsRevealing(true)
    // turn this back off after all
    // chars have been revealed
    setTimeout(() => {
      setIsRevealing(false)
      var element = document.getElementById('hints-box')
      if (element !== null) {
        element.scrollIntoView(false)
        element.scrollIntoView({ block: 'end' })
        element.scrollTop = element.scrollHeight
      }
      var element1 = document.getElementById('whole-div')
      if (element1 !== null) {
        // element1.scrollIntoView(false);
        // element1.scrollIntoView({block: "end"});
        element1.scrollTop = element1.scrollHeight
      }

      const scrollingElement = document.scrollingElement || document.body
      scrollingElement.scrollTop = scrollingElement.scrollHeight
    }, REVEAL_TIME_MS * 5)

    const winningWord = isWinningWord(currentGuess)

    if (guesses.length < MAX_CHALLENGES && !isGameWon) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        ReactGA.event({
          category: 'finished',
          action: 'finished_won',
          label: 'won',
          value: guesses.length + 1,
          nonInteraction: true,
        })
        setStats(addStatsForCompletedGame(stats, guesses.length))
        return setIsGameWon(true)
      }

      if (guesses.length === MAX_CHALLENGES - 1) {
        ReactGA.event({
          category: 'finished',
          action: 'finished_lost',
          label: 'lost',
          value: guesses.length + 1,
          nonInteraction: true,
        })
        setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        setIsGameLost(true)
        ReactNativeHapticFeedback.trigger('notificationError', options)
        showErrorAlert(CORRECT_WORD_MESSAGE(solution), {
          persist: true,
          delayMs: REVEAL_TIME_MS * MAX_WORD_LENGTH + 1,
        })
      }
    }

    var element = document.getElementById('hints-box')
    if (element !== null) {
      element.scrollIntoView(false)
      element.scrollIntoView({ block: 'end' })
      element.scrollTop = element.scrollHeight
    }
    var element1 = document.getElementById('whole-div')
    if (element1 !== null) {
      // element1.scrollIntoView(false);
      // element1.scrollIntoView({block: "end"});
      element1.scrollTop = element1.scrollHeight
    }

    const scrollingElement = document.scrollingElement || document.body
    scrollingElement.scrollTop = scrollingElement.scrollHeight
  }

  return (
    <div className="h-screen flex-col flex" id="whole-div">
      <Navbar
        setIsInfoModalOpen={setIsInfoModalOpen}
        setIsStatsModalOpen={setIsStatsModalOpen}
        setIsSettingsModalOpen={setIsSettingsModalOpen}
      />
      <div className="pt-2 px-1 md:max-w-7xl w-full mx-auto sm:px-6 lg:px-8 flex-col flex grow">
        <div className="pb-2 flex justify-center" id="hints-div">
          <Hints guesses={guesses} isGameWon={isGameWon} />
        </div>
        <div className="pb-2 grow">
          <Grid
            guesses={guesses}
            currentGuess={currentGuess}
            isRevealing={isRevealing}
            currentRowClassName={currentRowClass}
          />
        </div>

        <Keyboard
          onChar={onChar}
          onDelete={onDelete}
          onEnter={onEnter}
          guesses={guesses}
          isRevealing={isRevealing}
        />
        <InfoModal
          isOpen={isInfoModalOpen}
          handleClose={() => setIsInfoModalOpen(false)}
        />
        <StatsModal
          isOpen={isStatsModalOpen}
          handleClose={() => setIsStatsModalOpen(false)}
          guesses={guesses}
          gameStats={stats}
          isGameLost={isGameLost}
          isGameWon={isGameWon}
          handleShareToClipboard={() => showSuccessAlert(GAME_COPIED_MESSAGE)}
          isHardMode={isHardMode}
          isDarkMode={isDarkMode}
          isHighContrastMode={isHighContrastMode}
          numberOfGuessesMade={guesses.length}
        />
        <SettingsModal
          isOpen={isSettingsModalOpen}
          handleClose={() => setIsSettingsModalOpen(false)}
          isHardMode={isHardMode}
          handleHardMode={handleHardMode}
          isDarkMode={isDarkMode}
          handleDarkMode={handleDarkMode}
          isHighContrastMode={isHighContrastMode}
          handleHighContrastMode={handleHighContrastMode}
        />
        <AlertContainer />
      </div>
    </div>
  )
}

export default App
